import clsx from 'clsx';
import { useFormikContext } from 'formik';
import React, { useLayoutEffect } from 'react';

import { TextButton } from '@components/Button/variants';
import DateTimeRangePickerField from '@components/Form/DateTimeRangePickerField';
import FieldLabel from '@components/Form/FieldLabel';
import MultiValueAutocompleteField from '@components/Form/MultiValueAutocompleteField';
import TextField from '@components/Form/TextField';
import IconClose from '@components/Images/Rebranding/IconClose';
import { useEntitiesTop } from '@hooks';
import { autocompleteEntities, createEntity } from '@utils/backend-api/entities';
import { NewForumPost } from '@utils/backend-api/forumPosts';
import {
  DEFAULT_POLL_EXPIRATION_HOUR,
  DEFAULT_POLL_EXPIRATION_MINUTE,
  MAX_POLL_OPTIONS,
  MIN_POLL_OPTIONS,
  pollExpirationBounds,
  withLocalDefaultExpirationTime,
} from '@utils/backend-api/polls';
import { ENTITY_INPUT_SEARCH_THRESHOLD } from '@utils/constants';

import styles from './styles.module.scss';

function PollTagsField() {
  const { data: topEntityOptions, isLoading: isLoadingTopEntityOptions } = useEntitiesTop();

  return (
    <div className="w-full flex-1">
      <MultiValueAutocompleteField
        placeholder="Type tags"
        label="Tags"
        containerClassName="!mt-0 !flex !flex-col"
        name="entity_tags"
        fetchMethod={(value) =>
          autocompleteEntities({
            fragment: value,
            relationship_sources: ['chat_gpt', 'user_action'],
            order_entities_by: ['is_important', 'pdl_verified'],
          })
        }
        createMethod={(value) =>
          createEntity({
            name: value,
            entity_type: 'other',
            word_type: 'common',
          })
        }
        defaultOptions={isLoadingTopEntityOptions ? [] : topEntityOptions}
        inputLengthFetchThreshold={ENTITY_INPUT_SEARCH_THRESHOLD}
      />
    </div>
  );
}

export default function PollFieldsGroup({
  showTags = true,
  disabled = false,
}: {
  showTags?: boolean;
  disabled?: boolean;
}) {
  const { values, setFieldValue } = useFormikContext<NewForumPost>();
  const allOptions = values.poll_attributes?.poll_options_attributes || [];
  const visibleOptions = allOptions.filter((option) => !option._destroy);
  const isNewPollExpiration = allOptions.every((option) => !option.id);
  const { minDate, maxDate } = pollExpirationBounds();

  useLayoutEffect(() => {
    if (!isNewPollExpiration || disabled) return;
    const current = values.poll_attributes?.expires_at;
    const next = withLocalDefaultExpirationTime(current);
    if (next && next !== current) {
      setFieldValue('poll_attributes.expires_at', next);
    }
    // Keep 11:59 PM local on first paint for new polls; do not reset an in-progress edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddOption = () => {
    if (disabled || visibleOptions.length >= MAX_POLL_OPTIONS) return;
    setFieldValue('poll_attributes.poll_options_attributes', [
      ...allOptions,
      { text: '', position: visibleOptions.length },
    ]);
  };

  const handleRemoveOption = (index: number) => {
    if (disabled || visibleOptions.length <= MIN_POLL_OPTIONS) return;
    const option = allOptions[index];
    const nextOptions = option.id
      ? allOptions.map((currentOption, optionIndex) =>
          optionIndex === index ? { ...currentOption, _destroy: true } : currentOption
        )
      : allOptions.filter((_, optionIndex) => optionIndex !== index);

    let position = 0;
    const remappedOptions = nextOptions.map((currentOption) =>
      currentOption._destroy ? currentOption : { ...currentOption, position: position++ }
    );
    setFieldValue('poll_attributes.poll_options_attributes', remappedOptions);
  };

  return (
    <div className={styles.pollFieldsGroup}>
      <TextField
        className="mb-24"
        name="poll_attributes.question"
        label="Poll question"
        placeholder="Ask a question..."
        disabled={disabled}
      />
      <FieldLabel htmlFor="poll_attributes.poll_options_attributes">Options</FieldLabel>
      {allOptions.map((option, index) =>
        option._destroy ? null : (
          <div key={option.id ?? `new-${index}`} className={styles.pollOptionRow}>
            <TextField
              className="w-full"
              name={`poll_attributes.poll_options_attributes[${index}].text`}
              placeholder={`Option ${index + 1}`}
              disabled={disabled}
            />
            {!disabled && visibleOptions.length > MIN_POLL_OPTIONS && (
              <button
                type="button"
                className={clsx('unstyled', styles.removeOptionButton)}
                onClick={() => handleRemoveOption(index)}
                aria-label="Remove option"
              >
                <IconClose className="w-12" />
              </button>
            )}
          </div>
        )
      )}
      {!disabled && visibleOptions.length < MAX_POLL_OPTIONS && (
        <TextButton
          as="button"
          type="button"
          className={clsx('unstyled', styles.addOptionButton)}
          onClick={handleAddOption}
        >
          + Add option
        </TextButton>
      )}

      <div className="mt-24 flex max-w-full flex-col items-start gap-16 sm:flex-row">
        {showTags && <PollTagsField />}
        <div className="w-full flex-1">
          <DateTimeRangePickerField
            name="poll_attributes.expires_at"
            label="Poll expiration *"
            minDate={minDate}
            maxDate={maxDate}
            darkMode={true}
            disabled={disabled}
            initializeDefaultTime={isNewPollExpiration}
            defaultTime={{
              hour: DEFAULT_POLL_EXPIRATION_HOUR,
              minute: DEFAULT_POLL_EXPIRATION_MINUTE,
            }}
          />
        </div>
      </div>
    </div>
  );
}
